import type { FlagGuardrailVerdict, FlagKind } from 'definitions';
import type { FlagStatusLabel } from '@/state/flags/flag-status';
import { Archive, Check, Clock, Pause, Power, ShieldAlert, ShieldCheck, TrendingUp, CircleDot } from 'lucide-react';
import { cn } from 'helpers/cn';
import { FLAG_COPY } from './copy';

type ChipStyle = { icon: React.ComponentType<{ className?: string }>; chip: string };

/**
 * The Library's status semantics carried over: live is ok, a plan moving is the accent, a plan
 * waiting for a person is warn, and the states nothing is happening in are a plain hairline.
 */
const STATUS_CHIP: Record<FlagStatusLabel, ChipStyle> = {
  'Rolling out': { icon: TrendingUp, chip: 'border-accent-line text-accent' },
  Paused: { icon: Pause, chip: 'border-warn-line text-warn' },
  'Partly on': { icon: CircleDot, chip: 'border-ok-line text-ok' },
  'Fully on': { icon: Check, chip: 'border-ok-line text-ok' },
  Off: { icon: Power, chip: 'border-line text-dim' },
  Draft: { icon: Clock, chip: 'border-line text-dim' },
  Archived: { icon: Archive, chip: 'border-line text-dim' },
};

const CHIP_CLASS = 'micro-label rounded-pill inline-flex items-center gap-1.5 border px-2 py-1 whitespace-nowrap';

type FlagStatusChipProps = { status: FlagStatusLabel; className?: string };

export const FlagStatusChip = ({ status, className }: FlagStatusChipProps) => {
  const { icon: Icon, chip } = STATUS_CHIP[status];
  return (
    <span className={cn(CHIP_CLASS, chip, className)}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
};

/**
 * The guardrail's verdict in the same chips: no harm found is ok, a breach (which has paused
 * the plan) the danger the kill switch uses, and still collecting a plain hairline.
 */
const VERDICT_CHIP: Record<FlagGuardrailVerdict | 'none', ChipStyle> = {
  none: { icon: Clock, chip: 'border-line text-dim' },
  collecting: { icon: Clock, chip: 'border-line text-dim' },
  clear: { icon: Check, chip: 'border-ok-line text-ok' },
  pass: { icon: ShieldCheck, chip: 'border-ok-line text-ok' },
  breach: { icon: ShieldAlert, chip: 'border-danger-line bg-danger-bg text-danger' },
};

type GuardrailVerdictChipProps = { verdict: FlagGuardrailVerdict | null };

/** A null verdict is a step not checked yet. */
export const GuardrailVerdictChip = ({ verdict }: GuardrailVerdictChipProps) => {
  const { icon: Icon, chip } = VERDICT_CHIP[verdict ?? 'none'];
  return (
    <span className={cn(CHIP_CLASS, chip)}>
      <Icon className="h-3 w-3" />
      {verdict ? FLAG_COPY.guardrail.verdicts[verdict] : FLAG_COPY.guardrail.noCheckLabel}
    </span>
  );
};

/** The kind is a fact, not a status: the neutral chip the experiment type uses. */
export const KIND_CHIP_CLASS =
  'border-line bg-field text-dim shrink-0 rounded-pill border px-2 py-0.5 font-mono text-nano tracking-[0.1em] uppercase';

type FlagKindChipProps = { kind: FlagKind };

export const FlagKindChip = ({ kind }: FlagKindChipProps) => (
  <span className={KIND_CHIP_CLASS}>{FLAG_COPY.kind[kind]}</span>
);

type PlanStatus = keyof typeof FLAG_COPY.plan.statuses;

/** The plan's state in the same chips: moving is the accent, waiting for a person is warn. */
const PLAN_CHIP: Record<PlanStatus, ChipStyle> = {
  none: { icon: Clock, chip: 'border-line text-dim' },
  active: { icon: TrendingUp, chip: 'border-accent-line text-accent' },
  paused: { icon: Pause, chip: 'border-warn-line text-warn' },
  done: { icon: Check, chip: 'border-ok-line text-ok' },
};

type PlanStatusChipProps = { status: PlanStatus; byGuardrail: boolean };

/** The Plan card's status. Why it is paused is said on the status card, not here. */
export const PlanStatusChip = ({ status, byGuardrail }: PlanStatusChipProps) => {
  const { icon: Icon, chip } = byGuardrail ? VERDICT_CHIP.breach : PLAN_CHIP[status];
  return (
    <span className={cn(CHIP_CLASS, chip)}>
      <Icon className="h-3 w-3" />
      {byGuardrail ? FLAG_COPY.plan.statusGuardrail : FLAG_COPY.plan.statuses[status]}
    </span>
  );
};
